/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";

// Argon Dashboard 2 PRO MUI components

// Argon Dashboard 2 PRO MUI base styles
import whiteCurved from "assets/images/curved-images/white-curved.jpeg";
import typography from "assets/theme/base/typography";

// Images
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

function ComplexStatisticsCard({ image, color, icon, count, percentage, dropdown }) {
  const { size } = typography;

  return (
    <Card
      sx={({ functions: { linearGradient, rgba }, palette: { gradients } }) => ({
        backgroundImage: gradients[color]
          ? `${linearGradient(
              rgba(gradients[color].main, 0.8),
              rgba(gradients[color].state, 0.8)
            )}, url(${image || whiteCurved})`
          : `${linearGradient(
              rgba(gradients.dark.main, 0.8),
              rgba(gradients.dark.state, 0.8)
            )}, url(${image || whiteCurved})`,
        backgroundSize: image ? "cover" : "unset",
        padding: 2,
      })}
    >
      <ArgonBox p={2}>
        <Grid container>
          <Grid item xs={8}>
            <ArgonBox
              width="3rem"
              height="3rem"
              bgColor="linear-gradient(310deg, rgb(90, 110, 180), rgb(56, 57, 105))"
              borderRadius="xxl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color={color}
              shadow="md"
              fontSize={size.xl}
            >
              <Icon
                sx={{
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    gradients[color]
                      ? linearGradient(gradients[color].main, gradients[color].state)
                      : linearGradient(gradients.dark.main, gradients.dark.state),
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "white",
                }}
              >
                {icon}
              </Icon>
            </ArgonBox>
            <ArgonBox mt={2} lineHeight={0}>
              <ArgonTypography
                variant="h5"
                fontWeight="bold"
                color="white"
                sx={{ fontSize: "20px" }}
              >
                {count.number}
              </ArgonTypography>
              <ArgonTypography
                variant="button"
                fontWeight="regular"
                color="white"
                textTransform="capitalize"
              >
                {count.label}
              </ArgonTypography>
            </ArgonBox>
          </Grid>
          <Grid item xs={4}>
            <ArgonBox textAlign="right">
              {dropdown && (
                <ArgonBox mb={7} lineHeight={1} color="white">
                  <Icon fontSize="default" onClick={dropdown.action} sx={{ cursor: "pointer" }}>
                    more_horiz
                  </Icon>
                  {dropdown.menu}
                </ArgonBox>
              )}
              <ArgonTypography variant="button" fontWeight="bold" color="white" align="right">
                {percentage}
              </ArgonTypography>
            </ArgonBox>
          </Grid>
        </Grid>
      </ArgonBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  image: "",
  color: "dark",
  dropdown: false,
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  image: PropTypes.string,
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  count: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  percentage: PropTypes.string.isRequired,
  dropdown: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      action: PropTypes.func,
      menu: PropTypes.node,
    }),
  ]),
};

export default ComplexStatisticsCard;
